import {PayloadAction, createAsyncThunk, createSlice} from '@reduxjs/toolkit'

import config from '../../config'

import {AppState} from '../store'

import * as authService from '../../../../commons/src/services/auth'
import {isAdminModePermission} from '../../libs/user'
import * as userApi from '../../services/api/user'
import getInitialData from '../../services/init-data'

import {ExtendedCognitoUserWithPermission, ProviderId} from '../../types'

import {initialDataReceived} from './core'

export interface AuthState {
  initDone: boolean
  user: ExtendedCognitoUserWithPermission | null
  isAdminMode: boolean
}

const initialState: AuthState = {
  initDone: false,
  user: null,
  isAdminMode: false,
}

export const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setInitDone: (state, action: PayloadAction<{initDone: boolean}>) => {
      state.initDone = action.payload.initDone
    },
    setUser: (state, action: PayloadAction<{user: ExtendedCognitoUserWithPermission | null}>) => {
      state.user = action.payload.user
    },
    setIsAdminMode: (state, action: PayloadAction<{isAdminMode: boolean}>) => {
      state.isAdminMode = action.payload.isAdminMode
    },
  },
})

export const {setInitDone, setUser, setIsAdminMode} = authSlice.actions

export const signIn = createAsyncThunk(
  'auth/signIn',
  async (
    {user, providerId}: {user: ExtendedCognitoUserWithPermission; providerId?: ProviderId},
    {dispatch}
  ) => {
    if (providerId) {
      const initialData = await getInitialData(user.permission, providerId)
      dispatch(initialDataReceived(initialData))
    }

    dispatch(setUser({user}))
    dispatch(setIsAdminMode({isAdminMode: isAdminModePermission(user.permission)}))
    dispatch(setInitDone({initDone: true}))
  }
)

export const signOut = createAsyncThunk(
  'auth/signOut',
  async (global: boolean | undefined, {dispatch}) => {
    dispatch(setUser({user: null}))
    dispatch(setIsAdminMode({isAdminMode: false}))
    await authService.authSignOut(global)
  }
)

export const init = createAsyncThunk('auth/init', async (_, {dispatch, getState}) => {
  try {
    await authService.init(config.amplify)
    const currentUser = await authService.getCurrentAuthUser()
    const userAttributes = await authService.getUserAttributes()

    const userId = userAttributes.sub
    if (!userId) {
      throw new Error('Sign in failed (no user ID found)')
    }

    const userPermissionResponse = await userApi.getUserPermissions(userId)

    const user: ExtendedCognitoUserWithPermission = Object.assign(currentUser, {
      attributes: userAttributes,
      permission: userPermissionResponse.permissions,
    })
    const providerId = userPermissionResponse.provider || undefined

    if (!providerId && !isAdminModePermission(user.permission)) {
      // Triggers fail action creator which just ends initialisation and forces the user to sign in
      throw new Error('Sign in failed (incorrect permissions after init)')
    }

    dispatch(signIn({user, providerId}))
  } catch {
    dispatch(setInitDone({initDone: true}))
    return
  }

  authService.onAuthFailAfterVisibilityChange(() => {
    const currentState = getState() as AppState

    if (currentState.auth.user) {
      dispatch(signOut())
    }
  })
})

export default authSlice.reducer
